// Detect a failed JS/CSS chunk or dynamic-import load. Admin is a hand-deployed Vite SPA:
// a new deploy replaces the previous build's content-hashed chunks on S3, so a tab still
// running the OLD build gets a 404 when it next fetches a not-yet-loaded lazy route chunk.
// That surfaces as a ChunkLoadError / "Failed to fetch dynamically imported module" and
// breaks the page until a full reload pulls the new index.html.
export const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false
  const anyErr = err as any
  const name = String(anyErr.name || '')
  const message = String(anyErr.message ?? anyErr ?? '')
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\w-]+ failed/i.test(message) ||
    /Loading CSS chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    // A missing chunk that an SPA rewrite (e.g. Amplify's catch-all) serves as index.html
    // makes the browser reject the module for its text/html MIME type.
    /Expected a JavaScript(?:-or-Wasm)? module script but the server responded with a MIME type/i.test(
      message
    ) ||
    /disallowed MIME type/i.test(message)
  )
}

const CHUNK_RELOAD_KEY = 'app.chunkReloadAt'
const CHUNK_RELOAD_WINDOW_MS = 30000

// Reload once to pick up the new build (fresh index.html + new chunk hashes). Guarded via
// sessionStorage so it can NEVER loop: if we already reloaded within the window and still hit
// a chunk error, stop reloading and let the in-app error boundary show its recoverable UI.
// Returns true if a reload was triggered.
export const reloadOnceForChunkError = (): boolean => {
  try {
    const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || '0')
    const now = Date.now()
    if (now - last < CHUNK_RELOAD_WINDOW_MS) return false
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now))
    window.location.reload()
    return true
  } catch (_) {
    // sessionStorage unavailable — can't guard against a reload loop, so don't reload.
    return false
  }
}
